import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Home, BarChart3, Users, BookOpen, FileText, LogOut, MessageCircle } from 'lucide-react';

import OverviewTab from '../components/admin/OverviewTab';
import PendingListingsTab from '../components/admin/PendingListingsTab';
import AllListingsTab from '../components/admin/AllListingsTab';
import UsersTab from '../components/admin/UsersTab';
import BlogTab from '../components/admin/BlogTab';
import PagesTab from '../components/admin/PagesTab';
import MetricsTab from '../components/admin/MetricsTab';
import BuildersTab from '../components/admin/BuildersTab';
import EnquiriesTab from '../components/admin/EnquiriesTab';
import EditListingModal from '../components/admin/EditListingModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');
  const [analytics, setAnalytics] = useState(null);
  const [pendingListings, setPendingListings] = useState([]);
  const [allListings, setAllListings] = useState([]);
  const [users, setUsers] = useState([]);
  const [blogPosts, setBlogPosts] = useState([]);
  const [siteContent, setSiteContent] = useState({ 'terms-and-conditions': '', 'privacy-policy': '', 'about-us': '' });
  const [loading, setLoading] = useState(true);
  const [adminName, setAdminName] = useState('');
  const [editingListing, setEditingListing] = useState(null);

  useEffect(() => {
    const unifiedToken = localStorage.getItem('token');
    const user = localStorage.getItem('user');

    if (unifiedToken && user) {
      const userData = JSON.parse(user);
      if (userData.role === 'admin') {
        localStorage.setItem('admin_token', unifiedToken);
        localStorage.setItem('admin_name', userData.name);
        setAdminName(userData.name || 'Admin');
        fetchData();
        return;
      }
    }

    const token = localStorage.getItem('admin_token');
    const name = localStorage.getItem('admin_name');
    if (!token) { navigate('/login'); return; }
    setAdminName(name || 'Admin');
    fetchData();
  }, [navigate]);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const config = { headers: { Authorization: `Bearer ${token}` } };

      const [analyticsRes, pendingRes, allRes, usersRes, blogRes] = await Promise.all([
        axios.get(`${API}/admin/analytics`, config),
        axios.get(`${API}/admin/listings/pending`, config),
        axios.get(`${API}/admin/listings/all`, config),
        axios.get(`${API}/admin/users`, config).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/blog`, config).catch(() => ({ data: [] }))
      ]);

      setAnalytics(analyticsRes.data);
      setPendingListings(pendingRes.data);
      setAllListings(allRes.data);
      setUsers(usersRes.data);
      setBlogPosts(blogRes.data);

      const [tcRes, ppRes, aboutRes] = await Promise.all([
        axios.get(`${API}/site-content/terms-and-conditions`).catch(() => ({ data: { content: '' } })),
        axios.get(`${API}/site-content/privacy-policy`).catch(() => ({ data: { content: '' } })),
        axios.get(`${API}/site-content/about-us`).catch(() => ({ data: { content: '' } }))
      ]);
      setSiteContent({
        'terms-and-conditions': tcRes.data.content || '',
        'privacy-policy': ppRes.data.content || '',
        'about-us': aboutRes.data.content || ''
      });
    } catch (error) {
      console.error('Error fetching data:', error);
      if (error.response?.status === 401) {
        localStorage.removeItem('admin_token');
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        navigate('/login');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchAllListings = async (city) => {
    try {
      const token = localStorage.getItem('admin_token');
      const params = new URLSearchParams();
      if (city) params.append('city', city);
      const res = await axios.get(`${API}/admin/listings/all?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAllListings(res.data);
    } catch (error) {
      console.error('Error fetching listings:', error);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_name');
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  const tabs = [
    { id: 'overview', label: 'Overview' },
    { id: 'pending', label: `Pending Approval (${pendingListings.length})` },
    { id: 'all', label: 'All Listings' },
    { id: 'users', label: `Users (${users.length})`, icon: Users },
    { id: 'blog', label: `Blog (${blogPosts.length})`, icon: BookOpen },
    { id: 'pages', label: 'Pages', icon: FileText },
    { id: 'builders', label: 'Builders', icon: Home },
    { id: 'enquiries', label: 'Enquiries', icon: MessageCircle },
    { id: 'metrics', label: 'Metrics', icon: BarChart3 },
  ];

  return (
    <div className="min-h-screen bg-slate-50 overflow-x-hidden">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 px-6 md:px-12 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div
              className="w-10 h-10 rounded-full bg-blue-600 flex items-center justify-center cursor-pointer hover:bg-blue-700 transition-all"
              onClick={() => navigate('/')}
              data-testid="admin-logo-home"
            >
              <Home className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold cursor-pointer hover:text-blue-600 transition-colors" onClick={() => navigate('/')}>Admin Dashboard</h1>
              <p className="text-sm text-slate-600">Welcome, {adminName}</p>
            </div>
          </div>
          <button
            onClick={handleLogout}
            className="h-10 px-6 rounded-full bg-slate-100 text-slate-900 font-medium hover:bg-slate-200 transition-all flex items-center gap-2"
            data-testid="logout-button"
          >
            <LogOut className="w-4 h-4" />
            Logout
          </button>
        </div>
      </header>

      <div className="px-6 md:px-12 py-8">
        {/* Tabs */}
        <div className="flex gap-1 mb-8 border-b border-slate-200 overflow-x-auto scrollbar-hide" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-3 font-medium transition-all border-b-2 flex items-center gap-1.5 whitespace-nowrap text-sm flex-shrink-0 ${
                activeTab === tab.id
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-slate-600 hover:text-slate-900'
              }`}
              data-testid={`tab-${tab.id}`}
            >
              {tab.icon && <tab.icon className="w-4 h-4" />}
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        {activeTab === 'overview' && <OverviewTab analytics={analytics} setActiveTab={setActiveTab} />}
        {activeTab === 'pending' && <PendingListingsTab pendingListings={pendingListings} onRefresh={fetchData} onEditListing={setEditingListing} />}
        {activeTab === 'all' && <AllListingsTab allListings={allListings} onRefresh={fetchAllListings} onEditListing={setEditingListing} />}
        {activeTab === 'users' && <UsersTab users={users} />}
        {activeTab === 'blog' && <BlogTab blogPosts={blogPosts} onRefresh={fetchData} />}
        {activeTab === 'pages' && <PagesTab siteContent={siteContent} setSiteContent={setSiteContent} />}
        {activeTab === 'builders' && <BuildersTab />}
        {activeTab === 'enquiries' && <EnquiriesTab />}
        {activeTab === 'metrics' && <MetricsTab />}
      </div>

      {/* Edit Listing Modal */}
      {editingListing && (
        <EditListingModal
          listing={editingListing}
          onClose={() => setEditingListing(null)}
          onRefresh={fetchData}
        />
      )}
    </div>
  );
};

export default AdminDashboard;
